import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { freelanceDashboard, freelanceJobTabCounts, jobSummeryDetailsGet } from '../../redux/Thunk/FreelanceScreenThunk/FreelanceThunk';
import { Box, Card, CardContent, CardHeader, Chip, IconButton, Tab, Tabs, Typography } from '@mui/material';
import SearchField from '../../Components/Ui_elements/SearchField';
import GenericTable from '../../Components/Ui_elements/GenericTable';
import dayjs from 'dayjs';
import { Grid } from '@mui/material';
import { debounce } from 'lodash';
import { navTabsScoopFreelance } from '../../Helper/Constants';
import { TabPanel } from '../../Components/TabPanel';
import MessageIcon from '@mui/icons-material/Message';
import PopupModal from '../../Components/Ui_elements/PopupModal';
import Btn from '../../Components/Ui_elements/Btn';
import JobsPopup from './JobsPopup';
import TextsmsOutlinedIcon from "@mui/icons-material/TextsmsOutlined";

export const JobCommentCell = ({ text = "", limit = 25 }) => {
    const [open, setOpen] = useState(false);
    const trimmedText = text?.trim() || ""; // Handle null/undefined and trim whitespace
    const isEmpty = !trimmedText;
    const isLong = trimmedText.length > limit;

    if (isEmpty) return null; // Return null instead of empty Typography

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const preview = isLong ? trimmedText.slice(0, limit) + "..." : trimmedText;

    return (
        <>
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {preview}
                {isLong && (
                    <Typography
                        component="span"
                        variant="body2"
                        size="small"
                        onClick={handleOpen}
                        sx={{
                            textTransform: "none",
                            cursor: "pointer",
                            color: "blue",
                            display: "inline",
                            ml: 1
                        }}
                    >
                        Read more
                    </Typography>
                )}
            </Typography>

            <PopupModal
                maxWidth="sm"
                size="sm"
                width="650px"
                fullWidth
                open={open}
                handleClose={handleClose}
                title={<Typography variant="h6">Job Comment</Typography>}
            >
                <Typography variant="body2" sx={{ whiteSpace: "pre-line", pl: "6px" }}>
                    {trimmedText}
                </Typography>
            </PopupModal>
        </>
    );
};

const FreelanceDashboard = () => {
    const dispatch = useDispatch();
    const { counts, freelance_order_scoop, job_summary } = useSelector((state) => state.root.FreelanceModule);

    const { user } = useSelector((state) => state?.root?.authUser);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(100);
    const [order, setOrder] = useState({ column: null, dir: "asc" });
    const [searchTerm, setSearchTerm] = useState("");
    const [search, setSearch] = useState("");
    //const [tabValue, setTabValue] = useState('tab-requested'); // Default to 'requested' tab
    const [tabValue, setTabValue] = useState('tab-ongoing'); // Default to 'Ongoing' tab
    const [openJob, setOpenJob] = useState(false);
    const [jobId, setJobId] = useState(null);
    const [jobData, setJobData] = useState({});
    const [isChatOpen, setIsChatOpen] = useState(false);
    const freelancerId = user?.iUserId;
    const [locallyReadJobs, setLocallyReadJobs] = useState({});

    const fetchData = useCallback(async () => {
        try {
            await dispatch(
                freelanceDashboard({
                    id: user?.iUserId,
                    page: page,
                    perPage: pageSize,
                    tabName: tabValue, // Use the actual tab value here
                    search: search,
                    sortBy: order.column,
                    sortOrder: order.column ? order.dir.toUpperCase() : null,
                })
            );
        } catch (error) {
            console.error("Failed to fetch data:", error);
        }
    }, [tabValue, page, pageSize, order, search, dispatch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        dispatch(freelanceJobTabCounts(user?.iUserId));
    }, [dispatch]);

    const handleTabChange = (_, newValue) => {
        setTabValue(newValue);
    };

    const handleDebouncedSearch = useCallback(
        debounce((value) => {
            setSearch(value);
            setPage(0);
        }, 500),
        []
    );

    useEffect(() => {
        return () => {
            handleDebouncedSearch.cancel();
        };
    }, [handleDebouncedSearch]);

    const handleSearch = (value) => {
        setSearchTerm(value);
        handleDebouncedSearch(value);
    };

    const handleOpenJobsModal = (data) => {
        setOpenJob(true);
        setJobData(data);
        setJobId(data?.job_summmeryId);

        // ✅ PATCH: mark as read locally
        setLocallyReadJobs(prev => ({
            ...prev,
            [data?.job_summmeryId]: true,
        }));

        dispatch(jobSummeryDetailsGet(data?.job_summmeryId))
    };

    const columns = useMemo(
        () => [
            {
                name: "index",
                label: "#",
                options: {
                    customBodyRender: (_, tableMeta) => (
                        <Typography variant="body2" noWrap>
                            {tableMeta.rowIndex + 1}{" "}
                            {/* Row index starts from 0, so adding 1 */}
                        </Typography>
                    ),
                    setCellProps: () => ({
                        style: { width: "50px", },
                    }),
                },
            },
            {
                name: "poNumber",
                sortPramas: "poNumber",
                label: "Job Number",
                options: {
                    customBodyRender: (value, tableMeta) => {
                        const data = freelance_order_scoop?.data?.[tableMeta.rowIndex];
                        return (
                            <Grid
                                item
                                style={{ cursor: "pointer", color: "blue" }}
                                onClick={() => { handleOpenJobsModal(data); setIsChatOpen(false); }}
                            >
                                {value}
                            </Grid>
                        );
                    },
                    setCellProps: () => ({ style: { width: "100px" } }),
                },
            },
            {
                name: "IndirectClientName",
                sortPramas: "IndirectClientName",
                label: "Account",
                options: {
                    setCellProps: () => ({
                        style: { width: "50px", },
                    }),
                }
            },
            {
                name: "description",
                sortPramas: "description",
                label: "Comment",
                options: {
                    customBodyRender: (value) => {
                        const trimmedValue = value?.trim();
                        return trimmedValue ? <JobCommentCell text={trimmedValue} /> : null;
                    },
                    setCellProps: () => ({ style: { width: "200px" } }),
                },
            },
            {
                name: "due_date",
                label: "Due Date",
                sortPramas: "due_date",
                options: {
                    customBodyRender: (value) => {
                        if (value === "0000-00-00 00:00:00") {
                            return <Typography variant="body2"></Typography>;
                        }
                        const formattedDate = dayjs(value).format("DD.MM.YYYY");
                        const formattedTime = dayjs(value).format("HH:mm");
                        return (
                            <Typography variant="body2" noWrap>
                                {formattedDate} | {formattedTime}
                            </Typography>
                        );
                    },
                    setCellProps: () => ({
                        style: { width: "50px", },
                    }),
                },
            },
            {
                name: "item_status",
                sortPramas: "item_status",
                label: "Status",
                options: {
                    setCellProps: () => ({
                        style: { width: "50px", },
                    }),
                }
            },
            {
                name: "job_summmeryId",
                sortPramas: "job_summmeryId",
                label: "Messages",
                options: {
                    customBodyRender: (value, tableMeta) => {
                        const data = freelance_order_scoop?.data?.[tableMeta.rowIndex];

                        const hasComments = data?.comment_count > 0;

                        const isRead =
                            locallyReadJobs[data?.job_summmeryId] === true ||
                            data?.discussionReadId
                                ?.split(',')
                                .includes(freelancerId.toString());

                        const iconColor = !hasComments
                            ? "gray"
                            : isRead
                                ? "green"
                                : "red";

                        return (
                            <IconButton
                                onClick={() => {
                                    handleOpenJobsModal(data);
                                    setIsChatOpen(true);
                                }}
                            >
                                <TextsmsOutlinedIcon
                                    sx={{
                                        cursor: "pointer",
                                        color: iconColor,
                                        fontSize: 30,
                                    }}
                                />
                            </IconButton>
                        );
                    },
                    setCellProps: () => ({
                        style: { width: "100px" },
                    }),
                },
            }

        ],
        [freelance_order_scoop?.data, locallyReadJobs]
    );

    const refreshData = useCallback(() => {
        fetchData();
        dispatch(freelanceJobTabCounts(user?.iUserId));
    }, [fetchData, dispatch, user?.iUserId]);

    return (
        <>
            <Typography variant="h6" mb={1}>Jobs</Typography>
            <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="Freelance Jobs Tabs"
            >
                {navTabsScoopFreelance?.map((tab) => {
                    // Get the count directly using the countKey from the tab configuration
                    const count = counts?.data?.[tab.countKey] || 0;

                    return (
                        <Tab
                            key={tab.value}
                            value={tab.value}
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography textTransform="none">
                                        {tab.label}
                                    </Typography>
                                    <Chip
                                        label={count}
                                        sx={{
                                            ml: 1,
                                            backgroundColor: tabValue === tab.value ? '#fff' : 'primary.main',
                                            color: tabValue === tab.value ? 'primary.main' : 'white',
                                            border: tabValue === tab.value ? '1px solid primary.main' : 'none',
                                            '& .MuiChip-label': {
                                                px: 1,
                                            }
                                        }}
                                    />
                                </Box>
                            }
                        />
                    );
                })}
            </Tabs>


            <Card>
                <CardHeader
                    title=""
                    action={
                        <Box display={"flex"} alignItems={"center"} gap={2}>
                            <SearchField
                                onSearch={handleSearch}
                                placeholder="Search..."
                                size="small"
                                initialValue={searchTerm}
                            />
                        </Box>
                    }
                />
            </Card>

            <CardContent sx={{ padding: '0px' }}>
                <CardContent sx={{ padding: "0px" }}>
                    {navTabsScoopFreelance.map((tab) => (
                        <TabPanel key={tab.value} value={tabValue} index={tab.value}>
                            <GenericTable
                                columns={columns}
                                showAction={false}
                                data={freelance_order_scoop?.data || []}
                                options={{
                                    selectableRows: "none",
                                    serverSide: true,
                                    count: freelance_order_scoop?.total || 0,
                                    page,
                                    rowsPerPage: pageSize,
                                    rowsPerPageOptions: [10, 25, 50, 100],
                                    search: false,
                                    download: false,
                                    print: false,
                                    filter: false,
                                    viewColumns: false,
                                    sortOrder: {
                                        name: columns[order?.column]?.name || "", // safe fallback
                                        direction: order?.dir || "asc",
                                    },
                                    onTableChange: (action, tableState) => {
                                        switch (action) {
                                            case "changePage":
                                                setPage(tableState.page);
                                                break;
                                            case "changeRowsPerPage":
                                                setPageSize(tableState.rowsPerPage);
                                                setPage(0);
                                                break;
                                            case "sort":
                                                setOrder({
                                                    column: tableState.activeColumn,
                                                    dir: tableState.sortOrder.direction,
                                                });
                                                break;
                                            default:
                                                break;
                                        }
                                    },
                                }}
                            />
                        </TabPanel>
                    ))}
                </CardContent>
            </CardContent>


            {openJob && <JobsPopup jobId={jobId} data={job_summary?.data} chatData={jobData} tabValue={tabValue} openJob={openJob} handleOpenJobDiscussionPage={(data) => setOpenJob(data)} isChatOpen={isChatOpen} onRefresh={refreshData} />}

        </>
    );
};

export default FreelanceDashboard;